import React, { FC, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import {
  DailyTransactionTypeEnum,
  LookupItemModel,
  LookupTypeEnum,
  PageEnum,
  SearchPanelPropsModel,
  TransactionReportSearchParamsModel,
} from "../../../models";
import {
  getItemsByCategoryId,
  getItems,
} from "../../../serviceBroker/itemApiServiceBroker";
import { useLookups } from "../../../hooks";
import {
  getLookupByType,
  getReportTypes,
} from "../../../serviceBroker/lookupApiServiceBroker";
import { getLabelName } from "../../../utils";
import {
  ButtonBox,
  InputDatePicker,
  PrivilegesChecker,
  SelectBox,
  TextBox,
} from "../..";
import { AutoCompleteSearchBox } from "../../../pages/reports/items/AutoCompleteSearchBox";

export const ReportTransactionSearchPanel: FC<
  SearchPanelPropsModel<TransactionReportSearchParamsModel>
> = ({ setLoading, searchParams, setSearchParams, handelSearch }) => {
  //#region state
  const [isReportTypeShort, setIsReportTypeShort] = useState<boolean>(true);
  const { getLookupItems } = useLookups(setLoading, [
    LookupTypeEnum.ReportTypes,
    LookupTypeEnum.Customers,
    LookupTypeEnum.Supplier,
    LookupTypeEnum.Users,
    LookupTypeEnum.Employee,
    LookupTypeEnum.CostCenters,
    LookupTypeEnum.Currency,
    LookupTypeEnum.AllCategories,
    LookupTypeEnum.DaysOfWeek,
  ]);
  const [itemsLookUps, setItemsLookUps] = useState<LookupItemModel[]>([]);
  const [_, setStores] = useState<LookupItemModel[]>([]);
  const [__, setReportTypes] = useState<LookupItemModel[]>([]);
  const [customerLookUps, setCustomerLookUps] = useState<LookupItemModel[]>([]);
  useEffect(() => {
    const fetchCustomerLookUps = async () => {
      const results = await getLookupByType(LookupTypeEnum.Customers, true);
      setCustomerLookUps(results);
    };
    fetchCustomerLookUps();
  }, []);

  //#endregion
  //#region variables
  const supplierTransactionTypes = [
    DailyTransactionTypeEnum.PurchasesInvoice,
    DailyTransactionTypeEnum.PurchasesRefund,
  ];

  const customerTransactionTypes = [
    DailyTransactionTypeEnum.SalesInvoice,
    DailyTransactionTypeEnum.SalesRefund,
  ];

  useEffect(() => {
    if (searchParams.reportDesignView === 1 || !searchParams.reportDesignView) {
      setIsReportTypeShort(true);
    } else {
      setIsReportTypeShort(false);
    }
  }, [searchParams.reportDesignView]);
  //#endregion
  //#region useEffect
  useEffect(() => {
    const fillData = async () => {
      setLoading(true);
      const results = await getItemsByCategoryId(searchParams.categoryId);
      setItemsLookUps(results);
      setLoading(false);
    };
    fillData().then(() => {});
  }, [searchParams.categoryId]);
  useEffect(() => {
    const fillData = async () => {
      let storeList = await getLookupByType(LookupTypeEnum.Stores);
      setStores(storeList);
      let reportType = await getReportTypes();
      let filteredReportType: LookupItemModel[] = [];
      reportType.forEach((rt) => {
        filteredReportType.push({
          value: rt?.ID.toString(),
          name: rt?.Name,
          nameAr: getLabelName(rt?.Name),
        });
      });
      setReportTypes(filteredReportType);
    };
    fillData().then(() => {});
  }, []);

  const handleSearchCustomer = async (searchValue: string) => {
    if (!searchValue || searchValue.length < 2) return;

    setLoading(true);
    try {
      const results = await getLookupByType(
        LookupTypeEnum.Customers,
        true,
        false,
        searchValue === "none" ? [] : [{ key: "name", value: searchValue }]
      );
      setCustomerLookUps(results);
    } catch (error) {
      console.error("Error searching items:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchItems = async (searchValue: string) => {
    if (!searchValue || searchValue.length < 2) return;

    setLoading(true);
    try {
      const results = await getItems({
        categoryId: searchParams.categoryId,
        itemName: searchValue !== "none" ? searchValue : undefined,
      });
      setItemsLookUps(results);
    } catch (error) {
      console.error("Error searching items:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle item selection
  const handleItemSelected = (item: LookupItemModel | null) => {
    const obj = { ...searchParams };
    if (item == null) {
      obj.itemId = undefined;
    } else {
      obj.itemId =
        item.value === "" || item.value === null
          ? undefined
          : parseInt(item.value);
    }
    setSearchParams(obj);
  };

  //#endregion
  //#region html
  return (
    <>
      <Form>
        <div className="row row-cols-1 row-cols-xxl-3 row-cols-xl-3 row-cols-lg-3 row-cols-md-3 row-cols-sm-1 g-sm-2 g-md-4 align-items-start">
          <InputDatePicker
            className="form-control"
            //timeMode={InputDatePickerTimeDisplayModeEnum.TimeSelect}
            selectedDate={searchParams.fromDate}
            isClearable
            InputLabel={"From Date"}
            selectsStart
            startDate={searchParams.fromDate}
            endDate={searchParams.toDate}
            onChange={(date) => {
              const obj = { ...searchParams };
              obj.fromDate = date === null ? undefined : date;
              setSearchParams(obj);
            }}
          />
          <InputDatePicker
            className="form-control"
            selectedDate={searchParams.toDate}
            isClearable
            InputLabel={"To Date"}
            selectsEnd
            startDate={searchParams.fromDate}
            endDate={searchParams.toDate}
            minDate={searchParams.fromDate}
            onChange={(date) => {
              const obj = { ...searchParams };
              obj.toDate = date === null ? undefined : date;
              setSearchParams(obj);
            }}
          />
          <SelectBox
            labelName="Report Type"
            isSingleSelect={true}
            selectedValues={
              searchParams.reportDesignView
                ? [searchParams.reportDesignView.toString()]
                : [""]
            }
            source={getLookupItems(LookupTypeEnum.ReportTypes, true)}
            onStatusChange={(e: LookupItemModel) => {
              const obj = { ...searchParams };
              if (e == null) {
                obj.reportDesignView = undefined;
              } else {
                obj.reportDesignView =
                  e.value === "" || e.value === null
                    ? undefined
                    : parseInt(e.value);
              }
              setSearchParams(obj);
            }}
          />
          <AutoCompleteSearchBox
            id="customerSearchBox"
            labelName={
              supplierTransactionTypes.includes(
                searchParams.transactionType ??
                  DailyTransactionTypeEnum.SalesInvoice
              )
                ? "Supplier"
                : "Customer"
            }
            selectedValue={searchParams.custmerId?.toString()}
            items={customerLookUps}
            isSingleSelect={true}
            onItemSelected={(item) => {
              setSearchParams({ ...searchParams, custmerId: item?.value });
            }}
            onSearch={handleSearchCustomer}
          />

          <SelectBox
            labelName="User" //"Cashier"
            isSingleSelect={true}
            selectedValues={
              searchParams.userId ? [searchParams.userId.toString()] : [""]
            }
            source={getLookupItems(LookupTypeEnum.Users, true)}
            onStatusChange={(e: LookupItemModel) => {
              const obj = { ...searchParams };
              if (e == null) {
                obj.userId = undefined;
              } else {
                obj.userId =
                  e.value === "" || e.value === null
                    ? undefined
                    : parseInt(e.value);
              }
              setSearchParams(obj);
            }}
          />
          <SelectBox
            labelName="Employee"
            isSingleSelect={true}
            selectedValues={
              searchParams.employeeId
                ? [searchParams.employeeId.toString()]
                : [""]
            }
            source={getLookupItems(LookupTypeEnum.Employee, true)}
            onStatusChange={(e: LookupItemModel) => {
              const obj = { ...searchParams };
              if (e == null) {
                obj.employeeId = undefined;
              } else {
                obj.employeeId =
                  e.value === "" || e.value === null
                    ? undefined
                    : parseInt(e.value);
              }
              setSearchParams(obj);
            }}
          />
          <SelectBox
            labelName="Cost Center"
            isSingleSelect={true}
            selectedValues={
              searchParams.costCenterID
                ? [searchParams.costCenterID.toString()]
                : [""]
            }
            source={getLookupItems(LookupTypeEnum.CostCenters, true)}
            onStatusChange={(e: LookupItemModel) => {
              const obj = { ...searchParams };
              if (e == null) {
                obj.costCenterID = undefined;
              } else {
                obj.costCenterID =
                  e.value === "" || e.value === null
                    ? undefined
                    : parseInt(e.value);
              }
              setSearchParams(obj);
            }}
          />
          <SelectBox
            labelName="Currency"
            isSingleSelect={true}
            selectedValues={
              searchParams.currencyId
                ? [searchParams.currencyId.toString()]
                : [""]
            }
            source={getLookupItems(LookupTypeEnum.Currency, true)}
            onStatusChange={(e: LookupItemModel) => {
              const obj = { ...searchParams };
              if (e == null) {
                obj.currencyId = undefined;
              } else {
                obj.currencyId =
                  e.value === "" || e.value === null
                    ? undefined
                    : parseInt(e.value);
              }
              setSearchParams(obj);
            }}
          />
          {!isReportTypeShort ? (
            <SelectBox
              labelName="Category"
              isSingleSelect={true}
              selectedValues={
                searchParams.categoryId
                  ? [searchParams.categoryId.toString()]
                  : [""]
              }
              source={getLookupItems(LookupTypeEnum.AllCategories, true)}
              onStatusChange={(e: LookupItemModel) => {
                const obj = { ...searchParams };
                if (e == null) {
                  obj.categoryId = undefined;
                } else {
                  obj.categoryId =
                    e.value === "" || e.value === null
                      ? undefined
                      : parseInt(e.value);
                }
                obj.itemId = undefined;
                setSearchParams(obj);
              }}
            />
          ) : null}
          {!isReportTypeShort ? (
            <AutoCompleteSearchBox
              id="itemSearchBox"
              labelName={getLabelName("Item")}
              selectedValue={searchParams.itemId?.toString()}
              items={itemsLookUps}
              onItemSelected={handleItemSelected}
              onSearch={handleSearchItems}
            />
          ) : null}

          {!isReportTypeShort ? (
            <TextBox
              inputName={"ItemCode"}
              labelName="Item Code"
              inputValue={searchParams.itemCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const obj = { ...searchParams };
                if (
                  e.target.value === "" ||
                  e.target.value === null ||
                  e.target.value === undefined
                ) {
                  obj.itemCode = undefined;
                } else {
                  obj.itemCode = e.target.value;
                }
                setSearchParams(obj);
              }}
            />
          ) : null}

          <SelectBox
            labelName="Day of Week"
            isSingleSelect={true}
            selectedValues={
              searchParams.dayWeekOfSales
                ? [searchParams.dayWeekOfSales.toString()]
                : [""]
            }
            source={getLookupItems(LookupTypeEnum.DaysOfWeek, true)}
            onStatusChange={(e: LookupItemModel) => {
              const obj = { ...searchParams };
              if (e == null) {
                obj.dayWeekOfSales = undefined;
              } else {
                obj.dayWeekOfSales =
                  e.value === "" || e.value === null ? undefined : e.value;
              }
              setSearchParams(obj);
            }}
          />
        </div>
        <PrivilegesChecker formId={PageEnum.ReportSales} action="EnableSearch">
          <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12 d-flex justify-content-end">
            <ButtonBox
              iconType="magnify"
              className="btn-gradient-primary mt-3 btn-fw"
              variant="outline-primary"
              onClick={handelSearch}
            >
              {getLabelName("Search")}
            </ButtonBox>
          </div>
        </PrivilegesChecker>
      </Form>
    </>
  );
  //#endregion
};
